/* ===================================================================
   useTemplates() + useTemplateMutations() -- request-template CRUD
   (Brad, 2026-06-17).

   Read:   GET    /api/v1/templates
   Create: POST   /api/v1/templates
   Update: PUT    /api/v1/templates/{id}
   Delete: DELETE /api/v1/templates/{id}

   The Templates screen binds to these. After any write we invalidate the
   templates query so the list refetches from the DB. Read hook disabled
   until a Bearer rr_ token exists.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type Template,
  type TemplateInput,
  type TemplatesResponse,
  type GalleryQuery,
  type TemplateGalleryResponse,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useTemplates(): UseQueryResult<TemplatesResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.templates(),
    queryFn: ({ signal }) => api.listTemplates(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
}

/* ----- Template gallery (the deep bench) ----- */
export function useTemplateGallery(
  query?: GalleryQuery,
): UseQueryResult<TemplateGalleryResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.templateGallery(query as Record<string, unknown> | undefined),
    queryFn: ({ signal }) => api.listTemplateGallery(query, signal),
    enabled: hasToken,
    staleTime: 5 * 60_000,
  });
}

export interface UseAddFromGalleryResult {
  add: (galleryId: string) => Promise<{ template: Template; created: boolean }>;
  isPending: boolean;
  error: Error | null;
  pendingId: string | null;
}

/* Adds a catalog item to the tenant's own list. The from-gallery endpoint is
   idempotent: 201 on a fresh add, 200 if it was already there. We expose a
   `created` flag (derived from whether the returned template id was already in
   the tenant list) so the caller can pick the right toast copy. On success we
   invalidate the tenant templates query so the main list refetches. */
export function useAddFromGallery(): UseAddFromGalleryResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: async (galleryId: string) => {
      const before = qc.getQueryData<TemplatesResponse>(qk.templates());
      const beforeIds = new Set((before?.templates ?? []).map((t) => t.id));
      const res = await api.addTemplateFromGallery(galleryId);
      return { template: res.template, created: !beforeIds.has(res.template.id) };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.templates() });
    },
  });
  return {
    add: (galleryId) => m.mutateAsync(galleryId),
    isPending: m.isPending,
    error: m.error,
    pendingId: m.isPending ? (m.variables ?? null) : null,
  };
}

export interface UseTemplateMutationsResult {
  create: (input: TemplateInput) => Promise<Template>;
  update: (id: string, patch: TemplateInput) => Promise<Template>;
  remove: (id: string) => Promise<string>;
  isPending: boolean;
  error: Error | null;
}

export function useTemplateMutations(): UseTemplateMutationsResult {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.templates() });
  };
  const createM = useMutation({
    mutationFn: (input: TemplateInput) => api.createTemplate(input),
    onSuccess: invalidate,
  });
  const updateM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TemplateInput }) =>
      api.updateTemplate(id, patch),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteTemplate(id),
    onSuccess: invalidate,
  });
  return {
    create: (input) => createM.mutateAsync(input).then((r) => r.template),
    update: (id, patch) => updateM.mutateAsync({ id, patch }).then((r) => r.template),
    remove: (id) => deleteM.mutateAsync(id).then((r) => r.deleted),
    isPending: createM.isPending || updateM.isPending || deleteM.isPending,
    error: createM.error ?? updateM.error ?? deleteM.error ?? null,
  };
}
